'use client'

// Usamos Zod para la validación en lugar de EventRequest
import { Button } from '@/src/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/src/components/ui/form'
import { Input } from '@/src/components/ui/input'
import { Textarea } from '@/src/components/ui/textarea'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

// Esquema de validación con Zod
const cateringFormSchema = z.object({
  name: z.string().min(2, 'El nombre debe tener al menos 2 caracteres'),
  email: z.string().email('Ingresa un email válido'),
  phone: z.string().min(9, 'El teléfono debe tener al menos 9 caracteres'),
  date: z.string().min(1, 'Selecciona una fecha para el evento'),
  guests: z.coerce.number().min(1, 'El número de invitados debe ser al menos 1'),
  message: z.string().optional(),
})

// Tipo derivado del esquema
type CateringFormValues = z.infer<typeof cateringFormSchema>

function CateringForm() {
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false)

  // Inicializar el formulario con react-hook-form y zod
  const form = useForm<CateringFormValues>({
    resolver: zodResolver(cateringFormSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      date: '',
      guests: 0,
      message: '',
    },
    mode: 'onChange', // Validar al cambiar para mostrar errores en tiempo real
  })

  // Activar la validación en tiempo real
  form.watch()

  const onSubmit = async (data: CateringFormValues) => {
    setIsSubmitDisabled(true)

    try {
      const response = await fetch('/api/send-catering-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Error en el servidor')
      }

      if (result.error) {
        toast.error('Error', {
          description: result.error,
        })
        setIsSubmitDisabled(false)
      }
      else {
        // Mostrar mensaje de éxito y mantener el formulario con los datos
        toast.success('Solicitud enviada', {
          description: 'Gracias por tu interés. Te contactaremos pronto para coordinar los detalles.',
        })

        // Mantener el botón deshabilitado durante 5 segundos
        setTimeout(() => {
          setIsSubmitDisabled(false)
        }, 5000)
      }
    }
    catch (err: any) {
      toast.error('Error', {
        description: 'message' in err ? err.message as string : 'Error en el servidor',
      })
      setIsSubmitDisabled(false)
    }
  }

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <h3 className="text-2xl font-bold text-srperrito-dark mb-6">Solicitar información</h3>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nombre completo *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Tu nombre completo"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email *</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="tu@email.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Teléfono *</FormLabel>
                  <FormControl>
                    <Input
                      type="tel"
                      placeholder="Tu número de teléfono"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Fecha del evento *</FormLabel>
                  <FormControl>
                    <Input
                      type="date"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="guests"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Número aproximado de invitados *</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min="1"
                    placeholder="Número de invitados"
                    {...field}
                    onChange={(e) => {
                      const value = e.target.value === '' ? '0' : e.target.value
                      field.onChange(Number.parseInt(value, 10))
                    }}
                    value={field.value === 0 ? '' : field.value}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Detalles del evento</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Cuéntanos sobre tu evento, ubicación, preferencias..."
                    rows={4}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            type="submit"
            variant="primary"
            className="w-full"
            disabled={form.formState.isSubmitting || isSubmitDisabled}
          >
            {form.formState.isSubmitting ? 'Enviando...' : 'Enviar solicitud'}
          </Button>
        </form>
      </Form>
    </div>
  )
}

export default CateringForm
